import React, { DragEvent, useEffect, useRef, useState } from 'react';
import { AlertCircle, ImagePlus, Loader2 } from 'lucide-react';
import { uploadPoster } from '../../lib/eventsService';
import { errorMessage } from '../../lib/supabase';
import { preparePoster } from '../../utils/image';
interface PosterDropzoneProps {
  value: string;
  onChange: (url: string) => void;
  onBusyChange: (busy: boolean) => void;
  disabled?: boolean;
}
type Phase = 'idle' | 'preparing' | 'uploading';
export function PosterDropzone({
  value,
  onChange,
  onBusyChange,
  disabled = false
}: PosterDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  useEffect(() => {
    setPreviewFailed(false);
  }, [value]);
  const busy = phase !== 'idle';
  const handleFile = async (file: File | undefined) => {
    if (!file || busy) return;
    setError(null);
    setProgress(0);
    setPhase('preparing');
    onBusyChange(true);
    try {
      const blob = await preparePoster(file);
      setPhase('uploading');
      const url = await uploadPoster(blob, setProgress);
      onChange(url);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPhase('idle');
      onBusyChange(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    handleFile(event.dataTransfer.files[0]);
  };
  const openPicker = () => {
    if (!disabled && !busy) inputRef.current?.click();
  };
  return <div data-id="element-179" data-mp="31d6wpiye817w">
      <p className="mb-1.5 text-sm font-medium text-pine" data-id="element-180" data-mp="31dgqlhmk6siv">Poster</p>
      <div onDragOver={event => {
      event.preventDefault();
      if (!disabled) setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={handleDrop} className={`relative aspect-[3/4] w-full overflow-hidden rounded-sm border transition-colors ${dragging ? 'border-ember bg-ember/5' : 'border-pine/25 bg-creamDeep'} ${value ? '' : 'border-dashed'}`} data-id="element-181" data-mp="36bqi9i92razq">
        {value && !previewFailed ? <img src={value} alt="Current poster" onError={() => setPreviewFailed(true)} className="h-full w-full object-cover" data-id="element-182" data-mp="1b6himhq0uxdp" /> : <button type="button" onClick={openPicker} disabled={disabled || busy} className="flex h-full w-full flex-col items-center justify-center gap-3 p-5 text-center text-clay transition-colors hover:text-pine disabled:cursor-not-allowed" data-id="element-183" data-mp="ii3fxonzncvt">
            {previewFailed ? <>
                <AlertCircle className="h-6 w-6 text-ember" aria-hidden="true" data-id="element-184" data-mp="3vxrqgfxe5u25" />
                <span className="text-sm" data-id="element-185" data-mp="3lucodysbbdjr">This poster couldn’t be loaded. Upload it again.</span>
              </> : <>
                <ImagePlus className="h-6 w-6" aria-hidden="true" data-id="element-186" data-mp="3qnp8pwpyky6p" />
                <span className="text-sm" data-id="element-187" data-mp="3bxrilb8gxyev">
                  Drop the poster here
                  <br data-id="element-188" data-mp="3gv00dappg0fl" />
                  or <span className="font-medium text-pine underline underline-offset-4" data-id="element-189" data-mp="3gwb274zlay42">choose a file</span>
                </span>
              </>}
          </button>}

        {busy && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-cream/85 px-6" role="status" aria-live="polite" data-id="element-190" data-mp="dekkleizr1wo">
            <Loader2 className="h-5 w-5 animate-spin text-pine" aria-hidden="true" data-id="element-191" data-mp="3fbr0ms1fpau" />
            <span className="text-sm font-medium text-pine" data-id="element-192" data-mp="2j6zku9br91pf">
              {phase === 'preparing' ? 'Preparing…' : `Uploading ${progress}%`}
            </span>
            {phase === 'uploading' && <div className="h-1 w-full max-w-[10rem] overflow-hidden rounded-full bg-pine/15" data-id="element-193" data-mp="34f1y2qkqv4jo">
                <div className="h-full bg-pine transition-[width] duration-150 ease-out" style={{
            width: `${progress}%`
          }} data-id="element-194" data-mp="2zmii5myl3xbp" />
              </div>}
          </div>}
      </div>

      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" tabIndex={-1} aria-hidden="true" onChange={event => handleFile(event.target.files?.[0])} data-id="element-195" data-mp="2pnjao7pk27qa" />

      {value && <div className="mt-2 flex gap-4 text-sm" data-id="element-196" data-mp="3dd3ustxg3b15">
          <button type="button" onClick={openPicker} disabled={disabled || busy} className="font-medium text-pine underline decoration-pine/30 underline-offset-4 transition-colors hover:text-ember disabled:opacity-50" data-id="element-197" data-mp="38gdgoc57apid">
            Replace
          </button>
          <button type="button" onClick={() => onChange('')} disabled={disabled || busy} className="text-clay/80 transition-colors hover:text-ember disabled:opacity-50" data-id="element-198" data-mp="cjpdjps2p45x">
            Remove
          </button>
        </div>}

      {error && <p className="mt-2 text-sm text-ember" role="alert" data-id="element-199" data-mp="r8c3elfbtebe">
          {error}
        </p>}
    </div>;
}